import { useState, useEffect, useMemo } from 'react';
import { Typography, Divider, Box, List, LoginPrompt, CircularProgress } from 'src/components';
import UserListCard from '../components/user-list-card';
import FeatureListCard from '../components/feature-list-card';
import { useAuth, getUsers } from "src/utils";
import { useFeatureTravellerStore } from 'src/stores';

export default function FeaturesTravellersList({ features = [], onFeatureClick, travellersLoading, showFriendsOnly, onTravellerClick, allCountries, locationPermissionDenied }) {
    const increment = 10;

    const travellers = useFeatureTravellerStore((state) => state.travellers);
    const listViewVisibleFeatures = useFeatureTravellerStore((state) => state.listViewVisibleFeatures);
    const setListViewVisibleFeatures = useFeatureTravellerStore((state) => state.setListViewVisibleFeatures);
    const listViewVisibleTravellers = useFeatureTravellerStore((state) => state.listViewVisibleTravellers);
    const setListViewVisibleTravellers = useFeatureTravellerStore((state) => state.setListViewVisibleTravellers);
    const loadedTravellerData = useFeatureTravellerStore((state) => state.loadedTravellerData);
    const setLoadedTravellerData = useFeatureTravellerStore((state) => state.setLoadedTravellerData);

    const { user } = useAuth();
    const [loadingMore, setLoadingMore] = useState(false);

    const filteredTravellers = useMemo(() => {
        return showFriendsOnly
            ? travellers?.filter((u) => user?.friends?.includes(u?.id))
            : travellers;
    }, [showFriendsOnly, travellers, user?.friends]);

    const shuffledFeatures = useMemo(() => {
        if (!features || features.length === 0) return [];
        // Shallow copy then shuffle
        return [...features].sort(() => Math.random() - 0.5);
    }, [features]);

    useEffect(() => {
        if (!filteredTravellers || filteredTravellers.length === 0) return;

        const initialIds = filteredTravellers
            .slice(0, listViewVisibleTravellers)
            .map(u => u.id)
            .filter(id => !loadedTravellerData[id]); 

        if (initialIds.length > 0) {
            fetchTravellerDetails(initialIds);
        }
    }, [filteredTravellers, listViewVisibleTravellers, loadedTravellerData]);

    const handleLoadMoreTravellers = async () => {
        setLoadingMore(true);
        
        const nextVisible = listViewVisibleTravellers + increment;
        const newUsers = filteredTravellers.slice(listViewVisibleTravellers, nextVisible);

        const idsToFetch = newUsers
            .map(user => user.id)
            .filter(id => !loadedTravellerData[id]); 

        if (idsToFetch.length > 0) {
            await fetchTravellerDetails(idsToFetch);
        }

        setListViewVisibleTravellers(nextVisible);
        setLoadingMore(false);
    };

    async function fetchTravellerDetails(ids) {
        try {
            setLoadingMore(true);
            const users = await getUsers(ids); 
            const newData = {};
            users.forEach(user => {
                newData[user.id] = user;
            });
            setLoadedTravellerData(newData);
        } catch (error) {
            console.error("Failed to fetch traveller details:", error);
        } finally {
            setLoadingMore(false);
        }
    }

    return (
    <Box>
        <Box className="scrollBox" sx={{ px: 2, pb: 12, height: '100%' }}>
            
            {/* Features */}
            <Typography variant="h6" gutterBottom>
                {features.length > 0 ? features.length : ""} Places
            </Typography>

            {features.length > 0 ? (
                <>
                    <List dense>
                        {shuffledFeatures.slice(0, listViewVisibleFeatures).map((feature) => (

                            <FeatureListCard 
                                key={feature.properties.id} 
                                feature={feature.properties} 
                                coords={feature.geometry.coordinates}
                                onClick={onFeatureClick} 
                                allCountries={allCountries} />

                        ))}
                    </List>
                    {features.length > listViewVisibleFeatures && (
                        <Divider sx={{ my: 1, cursor: 'pointer' }} onClick={() => setListViewVisibleFeatures(listViewVisibleFeatures + increment)}>
                            <Typography variant="body2" color="textSecondary">
                                See more places
                            </Typography>
                        </Divider>
                    )}
                </>
            ): (
                <Box
                    sx={{
                        mt: 2,
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 1,
                        height: "50px",
                        justifyContent: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}>

                    <Typography variant="body2" fontWeight={600} sx={{ color: "text.secondary" }}>
                        No results available, try changing filters
                    </Typography>

                    </Box>
            )}

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                {filteredTravellers.length > 0 ? filteredTravellers.length : "Other"} Travellers
            </Typography>

            {/* Travellers */}
            {filteredTravellers.length > 0 && (
                <>
                    <List dense>
                        {filteredTravellers.slice(0, listViewVisibleTravellers).map((user) => {
                            const fullData = loadedTravellerData[user.id] || user;
                            return (
                                <UserListCard 
                                    key={user.id} 
                                    user={fullData} 
                                    onClick={onTravellerClick} 
                                    allCountries={allCountries} 
                                    />
                            );
                        })}
                    </List>
                    {filteredTravellers.length > listViewVisibleTravellers && (
                        <Divider sx={{ my: 1, cursor: 'pointer' }} onClick={handleLoadMoreTravellers}>
                            {loadingMore ? (
                                <CircularProgress size={20} />
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    See more travellers
                                </Typography>
                            )}
                        </Divider>
                    )}
                </>
            )}
            
            {(filteredTravellers.length === 0 || travellersLoading) && user && (
                <Box
                    sx={{
                        mt: 2,
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 1,
                        height: "100px",
                        justifyContent: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}>
                    
                    {travellersLoading ? (
                        <CircularProgress />
                    ) : (
                        <Typography variant="body2" fontWeight={600} sx={{ color: "text.secondary" }}>
                            {locationPermissionDenied ? "Enable Location Permissions to see Other Travellers."
                            :"Travellers view is unavaliable at the minute."}
                        </Typography>
                    )}

                    </Box>
            )}

            {!user && (
                <LoginPrompt 
                    message={"Log in to view other travellers."}
                    height={"100px"} />
            )}

        </Box>
    </Box>
    );
}
